// Clocktower Cars Gatwick — Policy content.
// Written to be clear, honest and reasonable. Not a substitute for legal advice.

export const POLICY_SECTIONS = [
  {
    slug: "terms",
    title: "Terms & Conditions",
    updated: "July 2026",
    intro:
      "These Terms & Conditions govern every booking placed with Clocktower Cars Gatwick (Churchill Court, Crawley, RH10 9KU). By booking with us — online, by phone, by WhatsApp, through our app or through an authorised third party — you accept these terms.",
    blocks: [
      {
        h: "1. Bookings and confirmations",
        p: "A booking is only confirmed once you have received written confirmation from us (email, SMS or app notification) showing the pickup address, time, vehicle and fare. Verbal quotes are indicative and become binding only on written confirmation.",
      },
      {
        h: "2. Fares and payment",
        p: "Fares are fixed and quoted before your journey. Additional charges may apply for out-of-hours pickups, additional stops, waiting time beyond the included allowance, tolls and any cleaning charges resulting from spillage, damage or sickness. We accept cash, all major cards, Apple Pay and Google Pay. Corporate account journeys are invoiced monthly.",
      },
      {
        h: "3. Waiting time",
        p: "For airport arrivals we track your flight and include 45 minutes of free waiting from the time your flight touches down. For all other bookings, up to 10 minutes free waiting is included from the confirmed pickup time. Beyond that, a per-minute charge applies at the vehicle's hourly rate.",
      },
      {
        h: "4. Cancellations and no-shows",
        p: "Cancellations more than 4 hours before pickup are free of charge. Cancellations within 4 hours may incur a fee up to the value of the journey. If our driver arrives and cannot locate you at the confirmed pickup or you are contactable but not present, this will be treated as a no-show and charged in full.",
      },
      {
        h: "5. Passenger conduct",
        p: "Our drivers may refuse or terminate any journey where a passenger is aggressive, intoxicated to the point of risk, in possession of illegal substances or otherwise unsafe. In such cases, the full fare remains payable and no refund is due.",
      },
      {
        h: "6. Vehicles, luggage and pets",
        p: "Please tell us at the time of booking about excess luggage, child seats, sports equipment, mobility aids or animals so we can send an appropriate vehicle. We reserve the right to upgrade to a larger vehicle at our discretion if standard luggage capacity is exceeded, and an additional charge may apply.",
      },
      {
        h: "7. Delays outside our control",
        p: "We plan every route with generous buffer time, but we are not liable for delays caused by traffic accidents, road closures, adverse weather, industrial action or other events outside our reasonable control. Where such delays occur we will do everything reasonable to get you to your destination safely.",
      },
      {
        h: "8. Liability",
        p: "Clocktower Cars Gatwick maintains full private-hire insurance, driver DBS checks and vehicle safety inspections in line with our licensing authority. Our liability is limited to direct losses arising from our negligence and is capped at the value of the affected journey. We are not liable for consequential losses (missed flights, missed meetings) where we have met the agreed pickup time.",
      },
      {
        h: "9. Complaints",
        p: "We take every complaint seriously. Please see our Complaints Policy below for how to raise a concern and our response timeframes.",
      },
      {
        h: "10. Governing law",
        p: "These terms are governed by the laws of England and Wales. Any dispute will be handled by the English courts.",
      },
    ],
  },
  {
    slug: "privacy",
    title: "Privacy Policy",
    updated: "July 2026",
    intro:
      "This Privacy Policy explains what personal data Clocktower Cars Gatwick collects, why, how we protect it and your rights under the UK GDPR and the Data Protection Act 2018. We are the data controller.",
    blocks: [
      {
        h: "What we collect",
        p: "When you book, contact us or use our app we collect: your name, phone number, email address, pickup and drop-off addresses, journey dates and times, vehicle preferences, any accessibility or child-seat requirements you share, flight number (where relevant), corporate account details, and payment card metadata (we never store full card numbers ourselves — payments are processed by our PCI-compliant provider).",
      },
      {
        h: "Why we use it",
        p: "To operate your booking, dispatch the correct vehicle, monitor your flight, send confirmations and receipts, provide customer service, handle complaints and lost property, meet our legal and licensing obligations, and (only if you have opted in) send occasional service updates and offers.",
      },
      {
        h: "Who we share it with",
        p: "Only where necessary: our licensed drivers (name, pickup, destination and any special requirements), our dispatch system provider, our card payment processor, our cloud hosting provider, our accountants for corporate invoicing, and law-enforcement or licensing authorities where legally required.",
      },
      {
        h: "How long we keep it",
        p: "Booking data is retained for 6 years to meet HMRC and licensing requirements. Complaint records are retained for 3 years. Marketing consent records are retained until you opt out plus one year. CCTV in vehicles, where fitted, is retained for a maximum of 31 days unless it evidences an incident.",
      },
      {
        h: "Your rights",
        p: "You can ask for a copy of your personal data, ask us to correct or delete it, restrict processing, object to marketing or ask us to move it to another provider. Contact bookings@clocktowercarsuk.com. We aim to reply within 30 days. You can also complain to the Information Commissioner's Office at ico.org.uk.",
      },
      {
        h: "Cookies",
        p: "Our website uses essential cookies to keep it working and, with your consent, analytics cookies to help us understand what visitors find useful. Please see our Cookies Policy for details.",
      },
    ],
  },
  {
    slug: "cookies",
    title: "Cookies Policy",
    updated: "July 2026",
    intro:
      "This site uses cookies and similar technologies. This policy explains what they are and how we use them.",
    blocks: [
      {
        h: "Strictly necessary cookies",
        p: "These make the site work — remembering your booking session, protecting against fraud, keeping you signed in to your account. They cannot be turned off.",
      },
      {
        h: "Analytics cookies",
        p: "With your consent we use privacy-friendly analytics to understand which pages are useful and where visitors are getting stuck. No personally identifying information is shared with third parties.",
      },
      {
        h: "Managing cookies",
        p: "You can clear or block cookies in your browser settings at any time. Blocking essential cookies may prevent parts of the site (like the booking flow) from working correctly.",
      },
    ],
  },
  {
    slug: "complaints",
    title: "Complaints Policy",
    updated: "July 2026",
    intro:
      "We aim to resolve every complaint fairly, quickly and honestly. If something has fallen short, please tell us so we can put it right and improve.",
    blocks: [
      {
        h: "How to make a complaint",
        p: "Fill in the online complaint form linked below, email bookings@clocktowercarsuk.com, or call the office on 01293 515151. Where possible please include your booking reference, the date and time, the driver or vehicle number and a clear description of what happened.",
      },
      {
        h: "Our response times",
        p: "We acknowledge every complaint within 2 working days. We aim to provide a full written response within 10 working days. Complex complaints (for example those involving CCTV review or third parties) may take longer, in which case we will keep you updated at least weekly.",
      },
      {
        h: "If you are not satisfied",
        p: "If you are not happy with our response you can escalate the complaint to our licensing authority, Crawley Borough Council Licensing Team (01293 438000, licensing@crawley.gov.uk). For data-protection concerns you can contact the Information Commissioner's Office at ico.org.uk.",
      },
    ],
    ctaLabel: "Submit a complaint",
    ctaHref: "/complaints",
  },
  {
    slug: "lost-property",
    title: "Lost Property Policy",
    updated: "July 2026",
    intro:
      "Every vehicle is checked between jobs, but occasionally items are left behind. Here is how we handle lost property.",
    blocks: [
      {
        h: "Reporting a lost item",
        p: "Please report the loss as soon as possible using the online form linked below or by calling the office on 01293 515151. Provide your name, the journey date and time, the pickup and drop-off addresses, and a clear description of the item. The sooner we know, the sooner the driver can check the vehicle.",
      },
      {
        h: "Retrieval and return",
        p: "Where an item is found we will contact you within 24 hours to arrange collection from our office in Crawley. If you would like the item posted back to you we will use tracked delivery and pass on the actual postage cost only. There is no handling fee.",
      },
      {
        h: "Retention and disposal",
        p: "Unclaimed items are held securely for 3 months. Perishable items, obviously worthless items and items presenting a safety or hygiene risk may be disposed of immediately at the driver's discretion. After 3 months unclaimed items are either donated to a local charity or disposed of responsibly.",
      },
      {
        h: "Valuables",
        p: "Please do not leave passports, wallets, phones, laptops or medication in the vehicle. Where we recover items of significant value we may ask for proof of ownership before returning them.",
      },
    ],
    ctaLabel: "Report a lost item",
    ctaHref: "/lost-property",
  },
  {
    slug: "cancellation",
    title: "Cancellation & Refund Policy",
    updated: "July 2026",
    intro:
      "We understand plans change. This policy explains cancellation windows, refunds and reschedules for private-hire bookings.",
    blocks: [
      {
        h: "Free cancellation window",
        p: "Standard bookings can be cancelled free of charge up to 4 hours before the confirmed pickup time. Airport pickups scheduled for early hours (00:00–06:00) can be cancelled free up to 6 hours before pickup.",
      },
      {
        h: "Late cancellations and no-shows",
        p: "Cancellations inside these windows may be charged up to the full journey value. If our driver has been dispatched or has arrived at the pickup and cannot locate the passenger, this is treated as a no-show and charged in full.",
      },
      {
        h: "Rescheduling",
        p: "Rescheduling within the free-cancellation window is always free. Beyond that we will do our best to accommodate reasonable changes, and any additional cost (for example a longer route) will be quoted before the change is confirmed.",
      },
      {
        h: "Refunds",
        p: "Where a refund is due we process it to the original payment method within 5 working days. Card refunds usually appear within 3-10 working days depending on the issuing bank.",
      },
    ],
  },
  {
    slug: "safeguarding",
    title: "Safeguarding & Passenger Conduct",
    updated: "July 2026",
    intro:
      "We are committed to the safety of every passenger, driver and member of the public. This policy sets out our safeguarding standards and what we expect from passengers.",
    blocks: [
      {
        h: "Our drivers",
        p: "Every Clocktower Cars driver is fully licensed by their local authority, holds an enhanced DBS certificate, has completed safeguarding awareness training and drives a vehicle that has passed annual private-hire inspections.",
      },
      {
        h: "Unaccompanied minors",
        p: "For safeguarding reasons we do not carry unaccompanied children under 12. School-run bookings under our regular contracts are subject to a separate written agreement with the parent, school or local authority.",
      },
      {
        h: "Zero tolerance",
        p: "We do not tolerate violence, sexual harassment, racism, homophobia, transphobia or any form of abuse — towards passengers or towards our drivers. Drivers may terminate a journey where such behaviour occurs, and any incident may be reported to the police and our licensing authority.",
      },
      {
        h: "CCTV",
        p: "Some of our vehicles are fitted with audio-visual CCTV for the safety of passengers and drivers. Where fitted, signage is displayed inside the vehicle. Footage is held securely and only reviewed where necessary for a safety incident, complaint investigation or lawful request.",
      },
    ],
  },
  {
    slug: "accessibility",
    title: "Accessibility Statement",
    updated: "July 2026",
    intro:
      "We want everyone to be able to travel with us. This statement explains our accessibility provision and how to get in touch if you have specific requirements.",
    blocks: [
      {
        h: "Wheelchair-accessible vehicles",
        p: "Our fleet includes wheelchair-accessible Mercedes V-Class vehicles with rear ramps and secure restraints. Please book in advance so we can allocate the correct vehicle and driver. There is no additional charge for wheelchair-accessible bookings.",
      },
      {
        h: "Assistance dogs",
        p: "Assistance dogs are always welcome in our vehicles, in line with the Equality Act 2010. Please mention the dog at the time of booking so we can prepare the vehicle appropriately.",
      },
      {
        h: "Hearing, visual and other requirements",
        p: "Please tell us at the time of booking if you would like the driver to communicate in a particular way, need help finding the vehicle, or have any other requirement we can support. Every driver is briefed on your notes before pickup.",
      },
      {
        h: "This website",
        p: "We aim to meet WCAG 2.1 AA standards. If you experience any accessibility issue on this site please email bookings@clocktowercarsuk.com and we will do our best to fix it and offer an alternative route to complete your booking in the meantime.",
      },
    ],
  },
];

export const POLICY_INDEX = POLICY_SECTIONS.map((p) => ({ slug: p.slug, title: p.title }));
